/*
 * album/index.ts - the photo album page (GALLERY_PLAN.md AL5 / AL6, §5). Wires the server-rendered Story view, the
 * sticky bar, the Grid view, the viewer and the favorites sheet together. The view lives in the URL
 * (?view=grid[&g=<slug>][&sg=<sub-group>][&src=pro|guest]), so reload / back / a shared link land where they were;
 * deep links (D15) open the viewer on their photo. A one-shot notice (LINE login, share code) becomes a toast.
 * No JS: everything below is skipped and the page is the plain server-rendered story.
 */

import { Favorites, UNGROUPED, loadData, photosOf, repaintHearts, toast } from "./shared";
import { Viewer } from "./viewer";
import { View, initAlbumNav } from "./nav";
import { initStory } from "./story";
import { Grid, GridFilter, parseSource } from "./grid";
import { FavoritesSheet } from "./favorites";

/** How long the page's one-shot notice stays up. */
const NOTICE_MS = 6000;

export function initAlbum(): void {
  const root = document.getElementById("album_root");
  const data = loadData();
  const dialog = document.getElementById("album_viewer") as HTMLDialogElement | null;
  if (!root || !data || !dialog || typeof dialog.showModal !== "function") return;

  const favs = new Favorites();
  const viewer = new Viewer(dialog, data, favs);
  const story = document.getElementById("album_story")!;
  const gridSection = document.getElementById("album_grid")!;
  const grid = new Grid(gridSection, data, favs, viewer);
  const sheetEl = document.getElementById("album_favs") as HTMLDialogElement | null;
  const sheet = sheetEl ? new FavoritesSheet(sheetEl, data, favs, viewer, document.getElementById("album-zip") as HTMLFormElement | null) : null;
  favs.onChange(() => repaintHearts(favs, data));

  let view: View = "story";

  /** The URL for a view + grid filter (a deep link's ?p= is dropped once the page moves on). */
  const urlFor = (v: View, f: GridFilter | null): URL => {
    const url = new URL(location.href);
    ["view", "g", "sg", "src", "p"].forEach((k) => url.searchParams.delete(k));
    if (v === "grid") {
      url.searchParams.set("view", "grid");
      if (f?.group) url.searchParams.set("g", f.group);
      if (f?.group && f.sub != null) url.searchParams.set("sg", String(f.sub));
      if (f && f.src !== "all") url.searchParams.set("src", f.src);
    }
    url.hash = "";
    return url;
  };

  /** Story or Grid; `f` = the grid filter to show (undefined = keep the grid's current one). */
  const setView = (v: View, f?: GridFilter, push = true): Promise<void> => {
    view = v;
    root.dataset.view = v;
    story.hidden = v !== "story";
    gridSection.hidden = v !== "grid";
    nav.paintView(v);

    const filter = v === "grid" ? f ?? grid.filter : null;
    if (push) {
      history.pushState({ albumView: v }, "", urlFor(v, filter));
      root.scrollIntoView({ block: "start" });
    }
    return filter ? grid.show(filter.group, filter.sub, filter.src) : Promise.resolve();
  };

  // chip changes rewrite the URL in place (no history entry per chip)
  grid.onFilter = (f) => {
    if (view === "grid") history.replaceState(history.state, "", urlFor("grid", f));
  };

  const nav = initAlbumNav(
    root,
    {
      setView: (v) => void setView(v),
      view: () => view,
      openFavorites: () => sheet?.open(),
    },
    () => favs.count,
    (f) => favs.onChange(f),
  );

  initStory(root, data, viewer, (slug) => void setView("grid", { group: slug || UNGROUPED, sub: null, src: "all" }));

  const fromQuery = (q: URLSearchParams, group: string | null): GridFilter => {
    const sg = q.get("sg");
    return { group, sub: sg && /^\d+$/.test(sg) ? Number(sg) : null, src: parseSource(q.get("src")) };
  };

  window.addEventListener("popstate", () => {
    const q = new URLSearchParams(location.search);
    viewer.close();
    sheetEl?.close();
    const v: View = q.get("view") === "grid" ? "grid" : "story";
    void setView(v, v === "grid" ? fromQuery(q, q.get("g")) : undefined, false);
  });

  // the one-shot notice: a toast with JS (the server-rendered line stays for no-JS)
  const notice = document.querySelector<HTMLElement>("[data-album-notice]");
  if (data.notice) {
    notice?.closest(".container")?.remove();
    window.setTimeout(() => toast(data.notice!, NOTICE_MS), 300);
  }

  // first paint: the server already checked the deep link against the visitor's access (D15)
  const deep = data.deep;
  const deepPhoto = deep.photo ? data.photos[deep.photo] : undefined;
  if (deep.view === "grid") {
    void setView("grid", fromQuery(new URLSearchParams(location.search), deep.group), false).then(() => {
      if (deepPhoto) grid.openAt(deepPhoto);
    });
  } else if (deepPhoto) {
    viewer.open(photosOf(data, [deepPhoto.id]), 0);
  }
}
